import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { defaultCountries, usePhoneInput } from "react-international-phone";
import useOptions from "../hooks/useOptions";
import { apiFetch } from "../common/api";
import { useTranslation } from "../i18n";

const HOST = import.meta.env.VITE_API_HOST;
const UPLOAD_URL = `${HOST}/api/v1/media-content/upload`;
const FILE_BASE = `${HOST}/api/v1/media-content/file`;

const INPUT_TYPE_MAP = {
  text: "text",
  number: "number",
  boolean: "checkbox",
  select: "select",
  phone: "phone",
  percent: "text",
};

function PhoneField({ value, onChange, label, required }) {
  const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: "ru",
    value,
    countries: defaultCountries,
    onChange: ({ phone }) => {
      onChange({ target: { value: phone } });
    },
  });

  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-red-200 ml-1">*</span>}
      </Label>
      <input
        type="tel"
        value={inputValue}
        ref={inputRef}
        onChange={handlePhoneValueChange}
        className="flex h-10 w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  );
}

function PercentField({ value, onChange, label, required, disabled }) {
  const handleChange = (evt) => {
    let percent = parseInt(evt.target.value.replace("%", ""));
    if (Number.isNaN(percent)) percent = 0;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;
    onChange({ target: { value: `${percent}%` } });
  };

  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-red-200 ml-1">*</span>}
      </Label>
      <Input
        value={
          typeof value === "number" && value <= 1
            ? `${value * 100}%`
            : value || "0%"
        }
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
}

function BooleanField({ value, onChange, label, disabled }) {
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={label}
        checked={!!value}
        disabled={disabled}
        onChange={(evt) =>
          onChange({ target: { checked: evt.target.checked } })
        }
      />
      <Label htmlFor={label}>{label}</Label>
    </div>
  );
}

function SelectField({ value, onChange, label, options, required, disabled }) {
  const { t } = useTranslation();
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-red-200 ml-1">*</span>}
      </Label>
      <Select
        value={value || ""}
        onChange={(evt) => onChange(evt)}
        disabled={disabled}
      >
        {required && (
          <option value="" disabled>
            {t("common.selectPlaceholder", {
              label: (label || "").toLowerCase(),
            })}
          </option>
        )}
        {options?.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

function ImageField({ value, onChange, label, disabled }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const uniqueName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${file.name}`;
      const uniqueFile = new File([file], uniqueName, { type: file.type });
      const formData = new FormData();
      formData.append("file", uniqueFile);
      const resp = await apiFetch(UPLOAD_URL, {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) throw new Error("Upload failed");
      const data = await resp.json();
      const url = `${FILE_BASE}/${data.filename}`;
      onChangeRef.current({ target: { value: url } });
      setPreviewUrl(null);
    } catch (err) {
      console.error("Upload error", err);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileChange = useCallback(
    (evt) => {
      const file = evt.target.files?.[0];
      if (file) doUpload(file);
      evt.target.value = "";
    },
    [doUpload],
  );

  useEffect(() => {
    const handleGlobalPaste = (evt) => {
      const items = evt.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type?.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            evt.preventDefault();
            doUpload(file);
            return;
          }
        }
      }
    };
    document.addEventListener("paste", handleGlobalPaste);
    return () => document.removeEventListener("paste", handleGlobalPaste);
  }, [doUpload]);

  const displayUrl = previewUrl || value;

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-col gap-2">
        {displayUrl && (
          <div className="relative w-full h-32 rounded-lg overflow-hidden bg-c-gray-600">
            <img
              src={displayUrl}
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading
              ? t("common.uploading")
              : value
                ? t("common.replace")
                : t("common.upload")}
          </Button>
          {value && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={disabled}
              onClick={() => onChange({ target: { value: "" } })}
            >
              {t("common.delete")}
            </Button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <div className="text-xs text-c-gray-400">
          {t("common.pasteFromClipboard")}
        </div>
      </div>
    </div>
  );
}

export default function DataForm({ fields, values, onChange, options }) {
  const { t } = useTranslation();
  const head = useMemo(
    () =>
      fields.map((f) => ({
        extractor: f.name,
        fetchOptions: f.fetchOptions,
      })),
    [fields],
  );
  const { options: resolvedOptions } = useOptions(head);

  const fieldOptions = {};
  for (const f of fields) {
    fieldOptions[f.name] = options?.[f.name] || resolvedOptions[f.name];
  }

  return (
    <div className="flex flex-col space-y-3">
      {fields.map((field) => {
        const label = t(field.label);
        const isDisabled = field.disabled != null ? field.disabled : false;
        const isRequired = field.required !== false;
        const currentValue = values?.[field.name];
        const opts = fieldOptions[field.name];

        if (field.hidden) return null;

        if (field.type === "phone") {
          return (
            <PhoneField
              key={field.name}
              label={label}
              value={currentValue}
              required={isRequired}
              onChange={(evt) =>
                onChange({ ...values, [field.name]: evt.target.value })
              }
            />
          );
        }

        if (field.type === "percent") {
          return (
            <PercentField
              key={field.name}
              label={label}
              value={currentValue}
              required={isRequired}
              disabled={isDisabled}
              onChange={(evt) =>
                onChange({ ...values, [field.name]: evt.target.value })
              }
            />
          );
        }

        if (field.type === "boolean") {
          return (
            <BooleanField
              key={field.name}
              label={label}
              value={currentValue}
              disabled={isDisabled}
              onChange={(evt) =>
                onChange({ ...values, [field.name]: evt.target.checked })
              }
            />
          );
        }

        if (field.type === "select") {
          return (
            <div key={field.name}>
              <SelectField
                label={label}
                value={currentValue}
                options={opts}
                required={isRequired}
                disabled={isDisabled}
                onChange={(evt) =>
                  onChange({ ...values, [field.name]: evt.target.value })
                }
              />
              {field.renderBelow?.(values)}
            </div>
          );
        }

        if (field.type === "image") {
          return (
            <ImageField
              key={field.name}
              label={label}
              value={currentValue}
              disabled={isDisabled}
              onChange={(evt) =>
                onChange({ ...values, [field.name]: evt.target.value })
              }
            />
          );
        }

        const inputType = INPUT_TYPE_MAP[field.type] || "text";
        const hasSuffix = field.suffix && currentValue;
        return (
          <div key={field.name}>
            <Label>
              {label}
              {isRequired && <span className="text-red-200 ml-1">*</span>}
              {isDisabled && (
                <span className="text-c-gray-400 ml-2 text-xs">
                  {t("common.readOnly")}
                </span>
              )}
            </Label>
            <div className="relative">
              <Input
                type={inputType}
                value={currentValue ?? field.default ?? ""}
                disabled={isDisabled}
                required={isRequired}
                onChange={(evt) =>
                  onChange({ ...values, [field.name]: evt.target.value })
                }
                className={hasSuffix ? "pr-9" : undefined}
              />
              {hasSuffix && (
                <button
                  type="button"
                  onClick={() => field.onSuffixClick?.()}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-c-red-500 hover:text-c-red-600"
                  aria-label={t("common.delete")}
                >
                  {field.suffix}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
